import React from 'react'
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  IconButton,
  Stack,
  Divider,
  Avatar,
  LinearProgress,
  Box,
  CircularProgress,
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { Invoice } from '../../types/payment'
import { getStatusConfig, StatusConfig } from './InvoiceStatusConfig'
import {
  calculatePaymentProgress,
  isInvoiceOverdue,
  getOutstandingAmount,
  formatInvoiceDate,
  formatCurrency,
} from '../../utils/invoiceUtils'

interface InvoiceCardProps {
  invoice: Invoice
  onCardClick: (id: string) => void
  onMenuClick: (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => void
  onPaymentClick: (invoice: Invoice) => void
  isActionLoading?: boolean
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({
  invoice,
  onCardClick,
  onMenuClick,
  onPaymentClick,
  isActionLoading = false,
}) => {
  const statusConfig: StatusConfig = getStatusConfig(invoice.status)
  const paymentProgress = calculatePaymentProgress(invoice)
  const overdueStatus = isInvoiceOverdue(invoice.due_date, invoice.status)
  const outstandingAmount = getOutstandingAmount(invoice)

  return (
    <Card
      role="article"
      aria-label={`Invoice ${invoice.invoice_number}`}
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onCardClick(invoice.id)
        }
      }}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: overdueStatus ? '2px solid' : 'none',
        borderColor: overdueStatus ? 'error.main' : 'transparent',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          transform: 'translateY(-2px)',
        },
        '&:focus': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '2px',
        },
      }}
      onClick={() => onCardClick(invoice.id)}
    >
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        {/* Header with Invoice Number and Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={600} color="primary" gutterBottom>
              {invoice.invoice_number}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              <Chip
                icon={statusConfig.icon as React.ReactElement}
                label={statusConfig.label}
                color={statusConfig.color}
                size="small"
                sx={{ borderRadius: 4 }}
              />
              {overdueStatus && (
                <Chip
                  icon={<WarningIcon fontSize="small" />}
                  label="Overdue"
                  color="error"
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 4 }}
                />
              )}
            </Stack>
          </Box>
          <IconButton
            size="small"
            aria-label={`More actions for invoice ${invoice.invoice_number}`}
            onClick={(e) => {
              e.stopPropagation()
              onMenuClick(e, invoice)
            }}
            sx={{ mt: -1, mr: -1 }}
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Customer Info */}
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
              <PersonIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                Customer
              </Typography>
              <Typography variant="body2" fontWeight={500} noWrap>
                {invoice.customer_name || invoice.customer?.name || 'N/A'}
              </Typography>
            </Box>
          </Box>

          {/* Source Sales Order */}
          {invoice.sales_order_number && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                  From Sales Order
                </Typography>
                <Typography variant="body2" fontWeight={500} color="info.main" noWrap>
                  {invoice.sales_order_number}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Dates */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Invoice Date
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={500}>
                {formatInvoiceDate(invoice.invoice_date)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: 14, color: overdueStatus ? 'error.main' : 'text.secondary' }} />
                <Typography variant="caption" color={overdueStatus ? 'error.main' : 'text.secondary'}>
                  Due Date
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                fontWeight={500}
                color={overdueStatus ? 'error.main' : 'text.primary'}
              >
                {formatInvoiceDate(invoice.due_date)}
              </Typography>
            </Box>
          </Box>

          {/* Amount and Payment Progress */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <MoneyIcon sx={{ fontSize: 14, color: 'success.main' }} />
                <Typography variant="caption" color="text.secondary">
                  Total Amount
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {paymentProgress.toFixed(0)}% paid
              </Typography>
            </Box>
            <Typography variant="h6" fontWeight={600} color="success.main" gutterBottom>
              ₹{formatCurrency(invoice.total_amount)}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={paymentProgress} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  backgroundColor: paymentProgress === 100 ? 'success.main' : 'info.main'
                }
              }}
            />
            {invoice.paid_amount > 0 && invoice.paid_amount < invoice.total_amount && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Paid: ₹{formatCurrency(invoice.paid_amount)} | 
                Balance: ₹{formatCurrency(outstandingAmount)}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
        <Button
          size="small"
          startIcon={<ViewIcon />}
          onClick={(e) => {
            e.stopPropagation()
            onCardClick(invoice.id)
          }}
          sx={{ textTransform: 'none' }}
          aria-label={`View invoice ${invoice.invoice_number}`}
        >
          View
        </Button>
        {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
          <Button
            size="small"
            startIcon={isActionLoading ? <CircularProgress size={16} /> : <PaymentIcon />}
            disabled={isActionLoading}
            onClick={(e) => {
              e.stopPropagation()
              onPaymentClick(invoice)
            }}
            sx={{ textTransform: 'none' }}
            color="success"
            aria-label={`Record payment for invoice ${invoice.invoice_number}`}
          >
            {isActionLoading ? 'Processing...' : 'Pay'}
          </Button>
        )}
      </CardActions>
    </Card>
  )
}

export default InvoiceCard
